import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  fetchOrderById,
  clearCurrentOrder,
} from "../../../Redux/custmoer_orders/slice";
import {
  ArrowLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileDown,
  FileText,
  MapPin,
  MessageCircleMore,
  MessageSquareText,
  PackageCheck,
  ReceiptText,
  RefreshCcw,
  Star,
  Truck,
} from "lucide-react";
import {
  formatCurrency,
  formatOrderDate,
  mapOrderToOrderRecord,
  toClassSlug,
} from "./ordersAdapters";
import "./MyOrdersPage.css";

const OrderDetails = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentOrder, loading, error } = useSelector(
    (state: RootState) => state.customerOrders,
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderById(id));
    }
    return () => {
      dispatch(clearCurrentOrder());
    };
  }, [id, dispatch]);

  if (loading) {
    return (
      <section className="orders-detail-page">
        <div className="orders-detail-shell">
          <div className="orders-mobile-empty">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="orders-detail-page">
        <div className="orders-detail-shell">
          <div className="orders-mobile-empty">
            <PackageCheck size={31} strokeWidth={1.9} />
            <strong>Something went wrong</strong>
            <span>{error}</span>
          </div>
        </div>
      </section>
    );
  }

  if (!currentOrder) {
    return (
      <section className="orders-detail-page">
        <div className="orders-detail-shell">
          <div className="orders-mobile-empty">
            <PackageCheck size={31} strokeWidth={1.9} />
            <strong>Order not found</strong>
            <span>The order could not be loaded.</span>
          </div>
        </div>
      </section>
    );
  }

  const record = mapOrderToOrderRecord(currentOrder);
  const item = record.items[0];
  const orderId = record.orderId;

  return (
    <section className="orders-detail-page">
      <div className="orders-detail-shell">
        <section className="orders-mobile-page orders-mobile-detail-page">
          <header className="orders-mobile-detail-header">
            <button
              type="button"
              className="orders-mobile-back"
              onClick={() => navigate("/MyOrders")}
              aria-label="Back to orders"
            >
              <ArrowLeft size={20} strokeWidth={2.2} />
            </button>
            <div>
              <span>ORDER</span>
              <h1>Order Details</h1>
            </div>
          </header>

          <div className="orders-mobile-detail-stack">
            <section className="orders-mobile-info-card">
              <div className="orders-mobile-info-row">
                <span>Order Placed</span>
                <strong>
                  {formatOrderDate(record.placedAt)}
                  {record.placedTime ? `, ${record.placedTime}` : ""}
                </strong>
              </div>
              <div className="orders-mobile-info-row">
                <span>Order Number</span>
                <strong className="order-number-value">
                  {record.orderNo || record.orderId}
                </strong>
              </div>
              <div className="orders-mobile-info-row">
                <span>Total</span>
                <strong>{formatCurrency(record.total)}</strong>
              </div>
              {record.paymentStatus === "Success" && (
                <button
                  type="button"
                  className="orders-mobile-row-link"
                  onClick={() => navigate(`/invoice/${orderId}`)}
                >
                  <span className="orders-mobile-row-icon">
                    <FileDown size={18} strokeWidth={2} />
                  </span>
                  <span>Download Invoice</span>
                  <ChevronRight size={19} strokeWidth={2} />
                </button>
              )}
            </section>

            <section className="orders-mobile-product-detail-card">
              <div className="orders-mobile-delivery-copy">
                <div>
                  <span
                    className={`orders-mobile-status-dot user-orders-status-${toClassSlug(
                      record.status,
                    )}`}
                  />
                  <h2>{record.statusTitle}</h2>
                </div>
                <p>{record.statusMessage}</p>
              </div>

              {record.items.map((orderItem, idx) => (
                <div
                  className="orders-mobile-detail-product"
                  key={orderItem.productId || idx}
                >
                  <div className="orders-mobile-detail-product-image">
                    <img
                      src={orderItem.image}
                      alt={orderItem.productName}
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3>{orderItem.productName}</h3>
                    {orderItem.sellerName && (
                      <p>
                        Sold by: <strong>{orderItem.sellerName}</strong>
                      </p>
                    )}
                    <span>
                      Qty: {orderItem.quantity} ·{" "}
                      {currentOrder.purchaseType || "ONLINE"}
                    </span>
                    <b>
                      {formatCurrency(orderItem.price || record.total)}
                    </b>
                  </div>
                </div>
              ))}

              <div className="orders-mobile-action-grid">
                <button
                  type="button"
                  className="primary"
                  onClick={() => navigate(`/TrackPackage/${orderId}`)}
                >
                  <Truck size={17} strokeWidth={2} />
                  <span>Track Order</span>
                </button>
                {item?.productId && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/SubmitReview/${item.productId}`)
                    }
                  >
                    <Star size={17} strokeWidth={2} />
                    <span>Write Product Review</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/SellerFeedBack")}
                >
                  <MessageCircleMore size={17} strokeWidth={2} />
                  <span>Seller Feedback</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/DeliveryFeedBack")}
                >
                  <MessageSquareText size={17} strokeWidth={2} />
                  <span>Delivery Feedback</span>
                </button>
              </div>
            </section>

            <section className="orders-mobile-menu-section">
              <div className="orders-mobile-section-heading">
                <span>
                  <ClipboardList size={18} strokeWidth={2} />
                </span>
                <h2>Order Info</h2>
              </div>
              <div className="orders-mobile-menu-card">
                {record.paymentStatus === "Success" && (
                  <button
                    type="button"
                    onClick={() => navigate(`/invoice/${orderId}`)}
                  >
                    <span className="orders-mobile-menu-icon">
                      <FileText size={18} strokeWidth={2} />
                    </span>
                    <span>Download Invoice</span>
                    <ChevronRight size={19} strokeWidth={2} />
                  </button>
                )}
                {record.refundAmount > 0 && (
                  <button type="button" disabled>
                    <span className="orders-mobile-menu-icon">
                      <RefreshCcw size={18} strokeWidth={2} />
                    </span>
                    <span>
                      Return / Refund Status: {record.refundStatus} (
                      {formatCurrency(record.refundAmount)})
                    </span>
                    <ChevronRight size={19} strokeWidth={2} />
                  </button>
                )}
              </div>
            </section>

            <section className="orders-mobile-simple-section">
              <div className="orders-mobile-section-heading">
                <span>
                  <CreditCard size={18} strokeWidth={2} />
                </span>
                <h2>Payment Method</h2>
              </div>
              <div className="orders-mobile-simple-card">
                {record.paymentMethod}
                {record.paymentStatus ? ` — ${record.paymentStatus}` : ""}
              </div>
            </section>

            <section className="orders-mobile-simple-section">
              <div className="orders-mobile-section-heading">
                <span>
                  <MapPin size={18} strokeWidth={2} />
                </span>
                <h2>Ship to</h2>
              </div>
              <address className="orders-mobile-address-card">
                <strong>{record.address.name || record.shipTo}</strong>
                {record.address.line1 && <span>{record.address.line1}</span>}
                {record.address.line2 && <span>{record.address.line2}</span>}
                {record.address.landmark && (
                  <span>Landmark: {record.address.landmark}</span>
                )}
                <span>
                  {[record.address.city, record.address.state]
                    .filter(Boolean)
                    .join(", ")}
                  {record.address.postalCode
                    ? ` - ${record.address.postalCode}`
                    : ""}
                  .
                </span>
                {record.address.country && (
                  <span>{record.address.country}</span>
                )}
                {record.address.phone && <span>{record.address.phone}</span>}
              </address>
            </section>

            <section className="orders-mobile-simple-section">
              <div className="orders-mobile-section-heading">
                <span>
                  <ReceiptText size={18} strokeWidth={2} />
                </span>
                <h2>Order Summary</h2>
              </div>
              <div className="orders-mobile-summary-card">
                <div>
                  <span>Item(s) Subtotal</span>
                  <strong>
                    {formatCurrency(record.summary.itemsSubtotal)}
                  </strong>
                </div>
                <div>
                  <span>Shipping</span>
                  <strong>{formatCurrency(record.summary.shipping)}</strong>
                </div>
                <div>
                  <span>Cash/Pay on Delivery fee</span>
                  <strong>
                    {formatCurrency(record.summary.cashOnDeliveryFee)}
                  </strong>
                </div>
                <div>
                  <span>Tax</span>
                  <strong>{formatCurrency(record.summary.tax)}</strong>
                </div>
                <div className="grand-total">
                  <span>Grand Total</span>
                  <strong>
                    {formatCurrency(record.summary.grandTotal)}
                  </strong>
                </div>
                {record.refundAmount > 0 && (
                  <>
                    <div>
                      <span>Refund Amount</span>
                      <strong>{formatCurrency(record.refundAmount)}</strong>
                    </div>
                    <div>
                      <span>Refund Status</span>
                      <strong>{record.refundStatus}</strong>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        </section>
      </div>
    </section>
  );
};

export default OrderDetails;
